import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LoadingSpinner, EmptyState } from '../../../components/layout/SharedComponents';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { useExpenditureLabels, useDecimalFormat } from '../../../contexts/ExpenditureLabelsContext';
import DataLookupField from '../../../components/DataLookupField';
import {
  Plus, Edit, Trash2, Save, Search, Download, X,
  FileText, Users, ChevronRight, ChevronDown, Bell,
  Lock, Eye, Pencil, Settings, Package
} from 'lucide-react';

export function SystemNotificationsPanel() {
  const [settings, setSettings] = useState(null);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/notification-settings');
      setSettings(res.data.settings || getDefaults());
      setEligibleUsers(res.data.eligible_users || []);
    } catch (err) { setSettings(getDefaults()); }
    setLoading(false);
  };

  const getDefaults = () => ({
    budget_notification: false, budget_notification_option: 'both',
    budget_percentage_alert: 100, budget_notify_user: '',
    in_app_notification: false,
    goods_received_notification: false, gr_reminder_days: 0, gr_recurring_reminder: false,
    supplier_doc_expiry_days: 0, supplier_doc_expiry_user: '',
    contract_notifications: false, rfp_rfq_notifications: false,
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/notification-settings', settings);
      toast.success('Notification settings saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const update = (field, value) => setSettings(p => ({ ...p, [field]: value }));

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-4xl" data-testid="system-notifications-panel">
      <h1 className="text-2xl font-bold mb-1">System Notifications</h1>
      <p className="text-lg font-medium text-muted-foreground mb-6">Notification Settings</p>

      {/* Section 1: Budget Notification */}
      <div className="border rounded-lg p-6 mb-6" data-testid="notif-budget-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">1</span>
          Budget Notification
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Budget Notification will be sent as an email alert when the budget for any Expenditure Code Exceeds the set limit.
        </p>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Label className="w-56 text-sm">Budget Notification</Label>
            <Switch checked={settings.budget_notification} onCheckedChange={v => update('budget_notification', v)} data-testid="notif-budget-toggle" />
            <span className={`text-xs font-medium ${settings.budget_notification ? 'text-green-600' : 'text-muted-foreground'}`}>
              {settings.budget_notification ? 'Yes' : 'No'}
            </span>
            <span className="text-xs text-muted-foreground">Select the switch to enable Budget Notification.</span>
          </div>
          {settings.budget_notification && (
            <>
              <div className="flex items-center gap-4">
                <Label className="w-56 text-sm">Notification option</Label>
                <select value={settings.budget_notification_option || 'both'} onChange={e => update('budget_notification_option', e.target.value)}
                  className="w-32 h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="notif-budget-option">
                  <option value="both">Both</option>
                  <option value="email">Email</option>
                  <option value="in_app">In-App</option>
                </select>
              </div>
              <div className="flex items-center gap-4">
                <Label className="w-56 text-sm">Set Budget Percentage (%) alert</Label>
                <select value={settings.budget_percentage_alert || 100} onChange={e => update('budget_percentage_alert', parseInt(e.target.value))}
                  className="w-20 h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="notif-budget-pct">
                  {[50, 60, 70, 75, 80, 85, 90, 95, 100].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
                <span className="text-sm">%</span>
              </div>
              <div className="flex items-center gap-4">
                <Label className="w-56 text-sm">Notify User</Label>
                <select value={settings.budget_notify_user || ''} onChange={e => update('budget_notify_user', e.target.value)}
                  className="w-52 h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="notif-budget-user">
                  <option value="">-- Select --</option>
                  {eligibleUsers.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                </select>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Section 2: In-App Notification */}
      <div className="border rounded-lg p-6 mb-6" data-testid="notif-inapp-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">2</span>
          In-App Notification
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Get real-time alerts in your browser whenever any approval is received.
        </p>
        <div className="flex items-center gap-4">
          <Label className="w-56 text-sm">In-App Notification</Label>
          <Switch checked={settings.in_app_notification} onCheckedChange={v => update('in_app_notification', v)} data-testid="notif-inapp-toggle" />
          <span className={`text-xs font-medium ${settings.in_app_notification ? 'text-green-600' : 'text-muted-foreground'}`}>
            {settings.in_app_notification ? 'Yes' : 'No'}
          </span>
          <span className="text-xs text-muted-foreground">Select the switch to enable In-App Notification.</span>
        </div>
      </div>

      {/* Section 3: Goods Received */}
      <div className="border rounded-lg p-6 mb-6" data-testid="notif-goods-received-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">3</span>
          Receiving
        </h3>
        {/* Book-In & Accept */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold mb-2">Receiving: Book-In & Accept Email Notifications</h4>
          <p className="text-xs text-muted-foreground mb-3">Please switch on the button to enable email notifications:</p>
          <div className="flex items-start gap-4 mb-4">
            <Label className="w-56 text-sm pt-1">Receiving Notification</Label>
            <Switch checked={settings.goods_received_notification} onCheckedChange={v => update('goods_received_notification', v)} data-testid="notif-gr-toggle" />
            <span className={`text-xs font-medium ${settings.goods_received_notification ? 'text-green-600' : 'text-muted-foreground'}`}>
              {settings.goods_received_notification ? 'Yes' : 'No'}
            </span>
            <p className="text-xs text-muted-foreground flex-1">
              Selecting this switch will send emails to the below selected people (Originator/Buyer/Delivery Contact) when goods are received, updated or deleted.
            </p>
          </div>
          {settings.goods_received_notification && (
            <div className="ml-4 pl-4 border-l-2 border-primary/20 space-y-3">
              <p className="text-xs text-muted-foreground mb-2">Select from the below options the people who should receive the email notifications:</p>
              {[
                { k: 'gr_notify_originator', l: 'Originator' },
                { k: 'gr_notify_buyer', l: 'Buyer' },
                { k: 'gr_notify_delivery_contact', l: 'Delivery Contact' },
              ].map(item => (
                <div key={item.k} className="flex items-center gap-3">
                  <Label className="w-36 text-sm">{item.l}</Label>
                  <Switch checked={settings[item.k] || false} onCheckedChange={v => update(item.k, v)} data-testid={`notif-${item.k}`} />
                  <span className={`text-xs font-medium ${settings[item.k] ? 'text-green-600' : 'text-muted-foreground'}`}>
                    {settings[item.k] ? 'Yes' : 'No'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Reminder Emails */}
        <div className="border-t pt-4">
          <h4 className="text-sm font-semibold mb-3">Receiving: Reminder Emails Notifications</h4>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <Label className="w-56 text-sm pt-1">Receiving Reminder Email Schedule (Days)</Label>
              <select value={settings.gr_reminder_days || 0} onChange={e => update('gr_reminder_days', parseInt(e.target.value))}
                className="w-16 h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid="notif-gr-reminder-days">
                {[0, 1, 2, 3, 5, 7, 14, 30].map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <p className="text-xs text-muted-foreground flex-1">
                Please select the number of days after which the Buyer and Requestor will be automatically alerted (via email) about items that have passed their due date in the order. The function will only trigger if the value {'>'} 0.
              </p>
            </div>
            <div className="flex items-start gap-4">
              <Label className="w-56 text-sm pt-1">Recurring Receiving Reminder Email Schedule</Label>
              <Switch checked={settings.gr_recurring_reminder} onCheckedChange={v => update('gr_recurring_reminder', v)} data-testid="notif-gr-recurring" />
              <span className={`text-xs font-medium ${settings.gr_recurring_reminder ? 'text-green-600' : 'text-muted-foreground'}`}>
                {settings.gr_recurring_reminder ? 'Yes' : 'No'}
              </span>
              <p className="text-xs text-muted-foreground flex-1">
                Selecting this switch will allow the system to continuously send email notifications in the selected number of days. The function will only trigger when GR Reminder Schedule (Days) {'>'} 0.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Section 4: Supplier Document Expiry */}
      <div className="border rounded-lg p-6 mb-6" data-testid="notif-supplier-doc-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">4</span>
          Supplier Document Expiry
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Supplier Document Expiry: The supplier can attach a document in the main application by entering the start date and end date.
          You can select the Reminder Email Schedule (Days) and Select User to notify the selected user about the supplier document expiry as per the selected reminder days.
        </p>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Label className="w-56 text-sm">Reminder Email Schedule (Days)</Label>
            <select value={settings.supplier_doc_expiry_days || 0} onChange={e => update('supplier_doc_expiry_days', parseInt(e.target.value))}
              className="w-16 h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid="notif-supplier-doc-days">
              {[0, 1, 2, 3, 5, 7, 14, 30, 60, 90].map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-4">
            <Label className="w-56 text-sm">Notify User</Label>
            <select value={settings.supplier_doc_expiry_user || ''} onChange={e => update('supplier_doc_expiry_user', e.target.value)}
              className="w-52 h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="notif-supplier-doc-user">
              <option value="">Select</option>
              {eligibleUsers.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Section 5: Contract Notifications */}
      <div className="border rounded-lg p-6 mb-6" data-testid="notif-contract-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">5</span>
          Contract Notifications
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          The system administrator can activate the Contract Notifications switch to ensure the Contract Manager receives an email notification when the Contract Value surpasses the specified 'Set Contract Percentage (%) alert' threshold.
        </p>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Label className="w-56 text-sm">Contract Notifications</Label>
            <Switch checked={settings.contract_notifications} onCheckedChange={v => update('contract_notifications', v)} data-testid="notif-contract-toggle" />
            <span className={`text-xs font-medium ${settings.contract_notifications ? 'text-green-600' : 'text-muted-foreground'}`}>
              {settings.contract_notifications ? 'Yes' : 'No'}
            </span>
            <span className="text-xs text-muted-foreground">Select the switch to enable Contract Notifications and set the Contract Percentage.</span>
          </div>
          {settings.contract_notifications && (
            <div className="flex items-center gap-4">
              <Label className="w-56 text-sm">Set Contract Percentage (%) alert</Label>
              <select value={settings.contract_percentage_alert || 0} onChange={e => update('contract_percentage_alert', parseInt(e.target.value))}
                className="w-20 h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="notif-contract-pct">
                {[0, 50, 60, 70, 75, 80, 85, 90, 95, 100].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <span className="text-sm">%</span>
            </div>
          )}
        </div>
      </div>

      {/* Section 6: RFP / RFQ Notifications */}
      <div className="border rounded-lg p-6 mb-6" data-testid="notif-rfp-rfq-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">6</span>
          RFP / RFQ Notifications
        </h3>
        <div className="space-y-6">
          <div className="flex items-start gap-4">
            <Label className="w-56 text-sm pt-1 flex-shrink-0">RFP Expiry Reminder to Buyer</Label>
            <select value={settings.rfp_expiry_reminder_days || 0} onChange={e => update('rfp_expiry_reminder_days', parseInt(e.target.value))}
              className="w-16 h-8 rounded-md border border-input bg-background px-2 text-xs flex-shrink-0" data-testid="notif-rfp-expiry-days">
              {[0, 1, 2, 3, 5, 7, 14, 30].map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <div className="text-xs text-muted-foreground flex-1">
              <p>Select the number of days from the dropdown after which the buyer will receive an email alert, notifying them of the remaining days before the RFP expires. Note that this feature will only activate if the selected value is greater than 0.</p>
              <p className="mt-1">For example, if the RFP end date is set to June 10, 2025, and the buyer reminder is configured for 2 days, the system will send an email reminder to the buyer on June 8, 2025, informing them that the RFP will expire in 2 days.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <Label className="w-56 text-sm pt-1 flex-shrink-0">RFQ Response Reminder to Supplier</Label>
            <select value={settings.rfq_response_reminder_days || 0} onChange={e => update('rfq_response_reminder_days', parseInt(e.target.value))}
              className="w-16 h-8 rounded-md border border-input bg-background px-2 text-xs flex-shrink-0" data-testid="notif-rfq-response-days">
              {[0, 1, 2, 3, 5, 7, 14, 30].map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <div className="text-xs text-muted-foreground flex-1">
              <p>Select the number of days from the dropdown after which a supplier will receive an email notification, informing them that an RFQ has been issued and requires their response. Note that this feature will only activate if the selected value is greater than 0.</p>
              <p className="mt-1">For example, if an RFQ is issued to a supplier on June 10, 2025, and the response reminder is set to 5 days, the system will send an email reminder to the supplier on June 15, 2025, if no response has been received.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} data-testid="notification-settings-save-btn">
          <Save className="h-4 w-4 mr-1" />{saving ? 'Saving...' : 'Update'}
        </Button>
      </div>
    </div>
  );
}
